import React, { useState, useEffect, useRef } from 'react';
import { FaTimes, FaTshirt } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';

const TagProductModal1 = ({
  isOpen,
  onClose,
  productType,
  setProductType,
  productItem,
  setProductItem,
  textboxUploadedImages,
  setTextboxUploadedImages
}) => {
  // Category options - easy to extend in the future
  const categoryOptions = ['Apparel'];
  
  // Item Detail options - easy to extend in the future
  const itemDetailOptions = ['Saree', 'Tshirt', 'Shirt'];
  const FREE_TEXT_OPTION = 'Enter custom SKU/Product Name';
  
  // Get ProductImgDtl from first uploaded image directly (no useMemo needed for simple access)
  const productImgDtl = textboxUploadedImages?.[0]?.ProductImgDtl || {};
  
  // Local state for form inputs
  const [localCategory, setLocalCategory] = useState(productType || categoryOptions[0] || '');
  const [localItem, setLocalItem] = useState(productItem || itemDetailOptions[0] || '');
  const [isFreeTextItem, setIsFreeTextItem] = useState(false);
  const [freeTextItem, setFreeTextItem] = useState('');
  const [localView, setLocalView] = useState(productImgDtl.view || 'Front View');
  const [localCoverage, setLocalCoverage] = useState(productImgDtl.coverage || 'Full Dress');
  const [localCloseupDetail, setLocalCloseupDetail] = useState(productImgDtl.closeupDetail || '');

  // Track if this is the initial mount to sync state properly
  const isInitialMount = useRef(true);

  // Sync with parent props when modal opens - runs on every render when isOpen is true
  // This ensures values are always restored from ProductImgDtl when modal opens
  useEffect(() => {
    if (isOpen) {
      const imgDtl = textboxUploadedImages?.[0]?.ProductImgDtl || {};
      // Set category to Apparel (default) or from productType if it exists
      setLocalCategory(productType || categoryOptions[0] || '');
      
      // Check if productItem is in the predefined options or is free text
      const itemValue = productItem || itemDetailOptions[0] || '';
      const isInOptions = itemDetailOptions.includes(itemValue);
      
      if (isInOptions) {
        setLocalItem(itemValue);
        setIsFreeTextItem(false);
        setFreeTextItem('');
      } else {
        // It's a free text value
        setLocalItem(FREE_TEXT_OPTION);
        setIsFreeTextItem(true);
        setFreeTextItem(itemValue);
      }
      
      setLocalView(imgDtl.view || 'Front View');
      // Restore coverage and closeupDetail from saved ProductImgDtl values
      setLocalCoverage(imgDtl.coverage || 'Full Dress');
      setLocalCloseupDetail(imgDtl.closeupDetail || '');
      isInitialMount.current = false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]); // Only depend on isOpen - we want this to run every time modal opens

  // Additional effect to sync when productType/productItem change while modal is open
  useEffect(() => {
    if (isOpen && !isInitialMount.current) {
      setLocalCategory(productType || categoryOptions[0] || '');
      
      // Check if productItem is in the predefined options or is free text
      const itemValue = productItem || itemDetailOptions[0] || '';
      const isInOptions = itemDetailOptions.includes(itemValue);
      
      if (isInOptions) {
        setLocalItem(itemValue);
        setIsFreeTextItem(false);
        setFreeTextItem('');
      } else {
        // It's a free text value
        setLocalItem(FREE_TEXT_OPTION);
        setIsFreeTextItem(true);
        setFreeTextItem(itemValue);
      }
    }
  }, [isOpen, productType, productItem]);

  // Check if category is apparel/clothing related
  const isApparelCategory = () => {
    const apparelKeywords = ['apparel', 'cloth', 'dress', 'shirt', 'pant', 'garment', 'outfit', 'attire'];
    const categoryLower = localCategory.toLowerCase();
    return apparelKeywords.some(keyword => categoryLower.includes(keyword));
  };

  const viewOptions = ['Front View', 'Back View', 'Side View (Left)', 'Side View (Right)'];
  const coverageOptions = ['Full Dress', 'Top Only', 'Bottom Only', 'Close-up'];

  // Handle apply/save
  const handleApply = () => {
    // Validation
    if (!localCategory.trim()) {
      return; // Category is mandatory
    }
    
    // Get the actual item value (either from dropdown or free text)
    const actualItemValue = isFreeTextItem ? freeTextItem.trim() : localItem.trim();
    if (!actualItemValue) {
      return; // Item is mandatory
    }

    // Apply to parent state
    setProductType(localCategory.trim());
    setProductItem(actualItemValue);
    
    // Update ProductImgDtl on the first uploaded image
    if (textboxUploadedImages && textboxUploadedImages.length > 0) {
      setTextboxUploadedImages(prevImages => {
        if (!prevImages || prevImages.length === 0) return prevImages;
        
        const updatedImages = [...prevImages];
        const existingImgDtl = updatedImages[0]?.ProductImgDtl || {};
        
        // Merge with existing ProductImgDtl to preserve any other fields
        const productImgDtl = {
          ...existingImgDtl,
          view: localView,
          coverage: localCoverage,
          closeupDetail: localCloseupDetail.trim()
        };
        
        updatedImages[0] = {
          ...updatedImages[0],
          ProductImgDtl: productImgDtl
        };
        
        return updatedImages;
      });
    }
    
    onClose();
  };

  // Handle clear individual tag
  const handleClearCategory = () => {
    setLocalCategory(categoryOptions[0] || '');
  };

  const handleClearItem = () => {
    setLocalItem(itemDetailOptions[0] || '');
    setIsFreeTextItem(false);
    setFreeTextItem('');
  };

  // Handle Item Detail dropdown change
  const handleItemDetailChange = (value) => {
    if (value === FREE_TEXT_OPTION) {
      setLocalItem(FREE_TEXT_OPTION);
      setIsFreeTextItem(true);
      setFreeTextItem('');
    } else {
      setLocalItem(value);
      setIsFreeTextItem(false);
      setFreeTextItem('');
    }
  };

  const handleClearView = () => {
    setLocalView('Front View');
  };

  const handleClearCoverage = () => {
    setLocalCoverage('Full Dress');
    setLocalCloseupDetail(''); // Also clear closeup detail when coverage is cleared
  };

  const handleClearCloseupDetail = () => {
    setLocalCloseupDetail('');
  };

  if (!isOpen) return null;

  const showApparelFields = isApparelCategory();
  const showCloseupDetail = localCoverage === 'Close-up';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="relative w-full max-w-md bg-gradient-to-br from-gray-900 to-black border border-white/10 rounded-2xl shadow-2xl overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center">
              <FaTshirt className="text-purple-400 text-lg" />
            </div>
            <div>
              <h2 className="text-lg sm:text-xl font-bold text-white">Tag Product</h2>
              <p className="text-xs text-gray-400 mt-0.5">Describe your product details</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors"
          >
            <FaTimes className="text-gray-400 hover:text-white" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6 space-y-4 max-h-[70vh] overflow-y-auto">
          
          {/* Category Dropdown */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-300 flex items-center gap-2">
              Category <span className="text-red-400">*</span>
            </label>
            <div className="relative">
              <select
                value={localCategory}
                disabled
                className="w-full px-4 py-2.5 pr-10 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 transition-all text-sm appearance-none cursor-not-allowed opacity-60"
              >
                {categoryOptions.map((option) => (
                  <option key={option} value={option} className="bg-gray-900">
                    {option}
                  </option>
                ))}
              </select>
              <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
            {localCategory && (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-purple-500/20 border border-purple-500/30 text-purple-300 text-xs">
                  #{localCategory}
                  <button onClick={handleClearCategory} className="hover:text-white">
                    <FaTimes className="text-[8px]" />
                  </button>
                </span>
              </div>
            )}
          </div>

          {/* Item Detail Dropdown */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-300 flex items-center gap-2">
              Item Detail <span className="text-red-400">*</span>
            </label>
            <div className="relative">
              <select
                value={localItem}
                onChange={(e) => handleItemDetailChange(e.target.value)}
                className="w-full px-4 py-2.5 pr-10 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 transition-all text-sm appearance-none cursor-pointer"
              >
                {itemDetailOptions.map((option) => (
                  <option key={option} value={option} className="bg-gray-900">
                    {option}
                  </option>
                ))}
                <option value={FREE_TEXT_OPTION} className="bg-gray-900">
                  {FREE_TEXT_OPTION}
                </option>
              </select>
              <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
            
            {/* Free Text Input - Show when "Free text" is selected */}
            {isFreeTextItem && (
              <div className="relative">
                <input
                  type="text"
                  value={freeTextItem}
                  onChange={(e) => setFreeTextItem(e.target.value)}
                  placeholder="Enter custom item detail..."
                  className="w-full px-4 py-2.5 pr-10 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 transition-all text-sm"
                />
                {freeTextItem && (
                  <button
                    onClick={() => setFreeTextItem('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                  >
                    <FaTimes className="text-xs" />
                  </button>
                )}
              </div>
            )}
            
            {/* Display selected item as tag */}
            {(isFreeTextItem ? freeTextItem : localItem) && (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-pink-500/20 border border-pink-500/30 text-pink-300 text-xs">
                  #{isFreeTextItem ? freeTextItem : localItem}
                  <button onClick={handleClearItem} className="hover:text-white">
                    <FaTimes className="text-[8px]" />
                  </button>
                </span>
              </div>
            )}
          </div>

          {/* View Dropdown - Only for apparel */}
          {showApparelFields && (
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-300">View</label>
              <div className="relative">
                <select
                  value={localView}
                  onChange={(e) => setLocalView(e.target.value)}
                  className="w-full px-4 py-2.5 pr-10 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 transition-all text-sm appearance-none cursor-pointer"
                >
                  {viewOptions.map((option) => (
                    <option key={option} value={option} className="bg-gray-900">
                      {option}
                    </option>
                  ))}
                </select>
                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-blue-500/20 border border-blue-500/30 text-blue-300 text-xs">
                  #{localView}
                  <button onClick={handleClearView} className="hover:text-white">
                    <FaTimes className="text-[8px]" />
                  </button>
                </span>
              </div>
            </div>
          )}

          {/* Coverage Dropdown - Only for apparel */}
          {showApparelFields && (
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-300">Coverage</label>
              <div className="relative">
                <select
                  value={localCoverage}
                  onChange={(e) => {
                    setLocalCoverage(e.target.value);
                    if (e.target.value !== 'Close-up') {
                      setLocalCloseupDetail('');
                    }
                  }}
                  className="w-full px-4 py-2.5 pr-10 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 transition-all text-sm appearance-none cursor-pointer"
                >
                  <option value="" className="bg-gray-900">Select coverage...</option>
                  {coverageOptions.map((option) => (
                    <option key={option} value={option} className="bg-gray-900">
                      {option}
                    </option>
                  ))}
                </select>
                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
              {localCoverage && (
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-cyan-500/20 border border-cyan-500/30 text-cyan-300 text-xs">
                    #{localCoverage}
                    <button onClick={handleClearCoverage} className="hover:text-white">
                      <FaTimes className="text-[8px]" />
                    </button>
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Closeup Detail - Only when coverage is Close-up */}
          {showApparelFields && showCloseupDetail && (
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-300">Closeup Detail</label>
              <div className="relative">
                <input
                  type="text"
                  value={localCloseupDetail}
                  onChange={(e) => setLocalCloseupDetail(e.target.value)}
                  placeholder="e.g., Collar, Buttons, Embroidery..."
                  className="w-full px-4 py-2.5 pr-10 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 transition-all text-sm"
                />
                {localCloseupDetail && (
                  <button
                    onClick={handleClearCloseupDetail}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                  >
                    <FaTimes className="text-xs" />
                  </button>
                )}
              </div>
              {localCloseupDetail && (
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-green-500/20 border border-green-500/30 text-green-300 text-xs">
                    #{localCloseupDetail}
                    <button onClick={handleClearCloseupDetail} className="hover:text-white">
                      <FaTimes className="text-[8px]" />
                    </button>
                  </span>
                </div>
              )}
            </div>
          )}

        </div>

        {/* Footer */}
        <div className="flex items-center gap-3 p-4 sm:p-6 border-t border-white/10 bg-black/20">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 rounded-lg border border-white/10 text-gray-300 hover:bg-white/5 transition-colors text-sm font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleApply}
            disabled={!localCategory.trim() || (!isFreeTextItem && !localItem.trim()) || (isFreeTextItem && !freeTextItem.trim())}
            className="flex-1 px-4 py-2.5 rounded-lg bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-medium transition-all text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Apply
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default TagProductModal1;

